import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Layers } from 'lucide-react';
import type { PlybookPly } from '@/types/plybook';
import { PLY_COLORS, MATERIAL_TYPE_LABELS, formatFiberAngle } from '@/types/plybook';
import { cn } from '@/lib/utils';

interface PlyStackViewerProps {
  plies: PlybookPly[];
  selectedPlyId?: string;
  onSelectPly?: (ply: PlybookPly) => void;
  className?: string;
}

export function PlyStackViewer({
  plies,
  selectedPlyId,
  onSelectPly,
  className,
}: PlyStackViewerProps) {
  // Sort plies by number (bottom to top)
  const sortedPlies = useMemo(
    () => [...plies].sort((a, b) => a.ply_number - b.ply_number),
    [plies]
  );

  // Reverse for visual display (top ply first in the stack view)
  const reversedPlies = useMemo(() => [...sortedPlies].reverse(), [sortedPlies]);

  if (plies.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Layers className="w-12 h-12 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">Sin capas definidas</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Vista de Stack ({plies.length} capas)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* 3D-like stack visualization */}
        <div className="relative">
          {/* Stack container */}
          <div className="flex flex-col-reverse gap-1">
            {sortedPlies.map((ply, index) => {
              const materialType = ply.material_type || 'other';
              const bgColor = ply.color || PLY_COLORS[materialType] || PLY_COLORS.other;
              const isSelected = ply.id === selectedPlyId;

              // Calculate offset for 3D effect
              const offset = index * 2;

              return (
                <div
                  key={ply.id}
                  className={cn(
                    'relative h-8 rounded-md transition-all cursor-pointer',
                    'hover:ring-2 hover:ring-primary/50 hover:z-10',
                    isSelected && 'ring-2 ring-primary z-10'
                  )}
                  style={{
                    backgroundColor: bgColor,
                    marginLeft: `${offset}px`,
                    marginRight: `${offset}px`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                  onClick={() => onSelectPly?.(ply)}
                >
                  <div className="absolute inset-0 flex items-center justify-between px-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white drop-shadow">
                        #{ply.ply_number}
                      </span>
                      <span className="text-xs text-white/90 drop-shadow hidden sm:inline">
                        {ply.material_name || MATERIAL_TYPE_LABELS[materialType]}
                      </span>
                    </div>
                    <Badge
                      variant="secondary"
                      className="text-xs bg-white/20 text-white border-0"
                    >
                      {formatFiberAngle(ply.fiber_angle)}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mold base indicator */}
          <div className="mt-2 h-3 bg-gradient-to-r from-gray-400 to-gray-500 rounded-md shadow-inner">
            <div className="text-center text-[10px] text-white/80 leading-3">
              MOLDE
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 pt-3 border-t">
          <p className="text-xs text-muted-foreground mb-2">Leyenda de materiales:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(PLY_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-1">
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs text-muted-foreground">
                  {MATERIAL_TYPE_LABELS[type as keyof typeof MATERIAL_TYPE_LABELS]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold">{plies.length}</p>
            <p className="text-xs text-muted-foreground">Capas</p>
          </div>
          <div>
            <p className="text-lg font-bold">
              {plies.reduce((sum, p) => sum + (p.weight_grams || 0), 0).toFixed(0)}g
            </p>
            <p className="text-xs text-muted-foreground">Peso</p>
          </div>
          <div>
            <p className="text-lg font-bold">
              {plies.reduce((sum, p) => sum + (p.area_m2 || 0), 0).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground">m² total</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Mini version for inline display
export function PlyStackMini({ plies }: { plies: PlybookPly[] }) {
  const sortedPlies = useMemo(
    () => [...plies].sort((a, b) => a.ply_number - b.ply_number),
    [plies]
  );

  return (
    <div className="flex flex-col-reverse gap-0.5">
      {sortedPlies.slice(0, 5).map((ply, index) => {
        const materialType = ply.material_type || 'other';
        const bgColor = ply.color || PLY_COLORS[materialType] || PLY_COLORS.other;

        return (
          <div
            key={ply.id}
            className="h-2 rounded-sm"
            style={{
              backgroundColor: bgColor,
              marginLeft: `${index}px`,
              marginRight: `${index}px`,
            }}
          />
        );
      })}
      {plies.length > 5 && (
        <div className="text-[10px] text-muted-foreground text-center">
          +{plies.length - 5} más
        </div>
      )}
    </div>
  );
}
